import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AsyncPipe, KeyValuePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  ButtonModule,
  DialogRef,
  IconModule,
  MultiSelectModule,
  OkDialog,
  SelectComponent,
  SimpleInputModule,
  SpinnerDirective,
  TextAreaComponentModule,
  ToSelectItemsPipe,
} from '@hglobal/common';
import { debounceTime, tap } from 'rxjs';
import { environment } from '@fb/environments';

import { ItSupportDialogService } from './it-support-dialog.service';
import { ITSupportRequest } from '../../models/it-support.model';
import { IssueField, IssueFieldConfig, IssueType } from '../../models/issues-type.model';
import { TranslateInPipe } from '../../pipes/translate-in.pipe';
import { FIELDS_CONFIG } from './constants/it-support-dialog.const';

interface FieldFormGroup {
  fieldId: FormControl<string>;
  label: FormControl<string>;
  required: FormControl<boolean>;
  isMultiSelect: FormControl<boolean>;
  allowedValues: FormControl<string[]>;
  parentFieldId: FormControl<string>;
  parentRelatedValues: FormControl<Record<string, string[]>>;
  fieldValue: any;
}

const getForm = (formBuilder: FormBuilder) => {
  return formBuilder.group({
    issueType: new FormControl(null, Validators.required),
    fields: new FormGroup<Record<string, FormGroup<FieldFormGroup>>>({}),
  });
};

@Component({
  selector: 'pu-it-support-dialog',
  templateUrl: './it-support-dialog.component.html',
  styleUrl: './it-support-dialog.component.scss',
  imports: [
    ButtonModule,
    IconModule,
    ReactiveFormsModule,
    AsyncPipe,
    SpinnerDirective,
    SelectComponent,
    SimpleInputModule,
    TextAreaComponentModule,
    ToSelectItemsPipe,
    TranslateInPipe,
    KeyValuePipe,
    MultiSelectModule,
  ],
  providers: [ItSupportDialogService],
})
export class ItSupportDialogComponent implements OnInit {
  readonly #dialogRef = inject(DialogRef);
  readonly #formBuilder = inject(FormBuilder);
  readonly #itSupportDialogService = inject(ItSupportDialogService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #okDialog = inject(OkDialog);
  readonly #changeDetectorRef = inject(ChangeDetectorRef);

  form = getForm(this.#formBuilder);
  issueTypes: IssueType[] = [];
  isLoading = this.#itSupportDialogService.isLoading;

  ngOnInit(): void {
    this.#initIssueTypes();
    this.#startIssueTypeControlWatching();
  }

  submit(): void {
    const { issueType, fields } = this.form.getRawValue();
    const payload: ITSupportRequest = {
      issueType,
      fields: Object.keys(fields)
        .filter(key => !!fields[key].fieldValue)
        .map(key => ({
          value: fields[key].fieldValue,
          fieldId: fields[key].fieldId,
        })),
    };

    this.#itSupportDialogService
      .sendRequest(payload)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.#dialogRef.close();

        this.#okDialog.open({
          headerKey: 'itSupportDialog.contactOkDialog.header',
          messageKey: 'itSupportDialog.contactOkDialog.message',
          subMessageKey: 'itSupportDialog.contactOkDialog.subMessage',
          btnOkKey: 'itSupportDialog.contactOkDialog.button.close',
          iconName: `${environment.shortName}-ai-chat-bot`,
          noCancel: true,
        });
      });
  }

  close(): void {
    this.#dialogRef.close();
  }

  valueChange(fieldId: string): void {
    const curFieldValue = this.form.controls.fields.controls[fieldId].value.fieldValue;

    Object.values(this.form.controls.fields.controls).forEach(formGroup => {
      if (formGroup.controls.parentFieldId.value === fieldId) {
        formGroup.controls.fieldValue.reset();
      }

      if (formGroup.controls.parentFieldId.value) {
        if (formGroup.controls.parentRelatedValues.value[curFieldValue]) {
          formGroup.controls.required.setValue(true);
          formGroup.controls.fieldValue.addValidators([Validators.required]);
          formGroup.controls.fieldValue.updateValueAndValidity();
        } else {
          formGroup.controls.required.setValue(false);
          formGroup.controls.fieldValue.removeValidators([Validators.required]);
          formGroup.controls.fieldValue.updateValueAndValidity();
        }
      }
    });

    this.#changeDetectorRef.detectChanges();
  }

  #initIssueTypes(): void {
    this.#itSupportDialogService
      .getIssueTypes()
      .pipe(
        tap(issueTypes => {
          this.issueTypes = issueTypes.filter(el => FIELDS_CONFIG[el.issueType]);
          this.#changeDetectorRef.detectChanges();
        }),
      )
      .subscribe();
  }

  #startIssueTypeControlWatching(): void {
    this.form.controls.issueType.valueChanges
      .pipe(
        debounceTime(300),
        tap(issueType => {
          Object.keys(this.form.controls.fields.controls).forEach(controlName => {
            this.form.controls.fields.removeControl(controlName);
          });
          this.#changeDetectorRef.detectChanges();
          this.#updateFormFields(issueType);
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #updateFormFields(issueType: string): void {
    const config = FIELDS_CONFIG[issueType];
    const fields = (this.issueTypes.find(el => el.issueType === issueType)?.fields ?? []).filter(el => config.find(configField => configField.fieldId === el.fieldId));

    fields.forEach(field => {
      this.form.controls.fields.addControl(field.fieldId, this.#createFormGroup(field, config));
    });

    this.#changeDetectorRef.detectChanges();
  }

  #createFormGroup(field: IssueField, config: IssueFieldConfig[]): FormGroup<FieldFormGroup> {
    const fieldConfig = config.find(el => el.fieldId === field.fieldId);
    const allowedValueFiltersSet = new Set(fieldConfig?.allowedValues ?? []);
    const allowedValues = allowedValueFiltersSet.size
      ? field.allowedValues.filter(el => allowedValueFiltersSet.has(el))
      : field.allowedValues;

    return this.#formBuilder.group<FieldFormGroup>({
      fieldId: new FormControl(field.fieldId),
      label: new FormControl(field.name),
      required: new FormControl(field.required || fieldConfig.isRequired),
      allowedValues: new FormControl(allowedValues),
      parentFieldId: new FormControl(fieldConfig?.parent?.fieldId),
      parentRelatedValues: new FormControl(fieldConfig?.parent?.values ?? {}),
      isMultiSelect: new FormControl(field.fieldType === 'array' || false),
      fieldValue: new FormControl(null, field.required || fieldConfig.isRequired ? [Validators.required] : []),
    });
  }
}
